import {
  Wifi120Regular,
  Wifi220Regular,
  Wifi320Regular,
  Wifi420Regular,
  WifiWarning20Regular,
} from "@fluentui/react-icons";
import type { FluentIcon } from "@fluentui/react-icons";
import { useEffect, useState } from "react";

import { useFluid } from "../../features/fluid/hooks/use-fluid.js";

const wifiIcons = [
  Wifi420Regular,
  Wifi320Regular,
  Wifi220Regular,
  Wifi120Regular,
];

const ANIMATION_STEP_DELAY_MS = 200;
const TOOLTIP_TEXT = {
  connected: "Connected to Fluid container",
  connecting: "Reconnecting to Fluid container",
  disconnected: "Disconnected from Fluid container",
} as const;

export const FluidConnectionStatusIndicator = () => {
  const { connectionState, retry } = useFluid();
  const [animationStep, setAnimationStep] = useState(0);

  useEffect(() => {
    if (connectionState !== "connecting") {
      setAnimationStep(0);
      return;
    }

    const interval = setInterval(() => {
      setAnimationStep((prev) => (prev + 1) % wifiIcons.length);
    }, ANIMATION_STEP_DELAY_MS);

    return () => clearInterval(interval);
  }, [connectionState]);

  const getIcon = (): FluentIcon => {
    if (connectionState === "disconnected") {
      return WifiWarning20Regular;
    }
    if (connectionState === "connecting" && animationStep < wifiIcons.length) {
      return wifiIcons[animationStep] as FluentIcon;
    }

    return Wifi120Regular;
  };

  const getIconColor = () => {
    if (connectionState === "disconnected") {
      return "#ffffff";
    }
    if (connectionState === "connecting") {
      return "#eab308";
    }

    return "#22c55e";
  };

  const getBackgroundColor = () => {
    if (connectionState === "disconnected") {
      return "#ef4444";
    }

    return "#ffffff";
  };

  const showReconnectButton = connectionState === "disconnected";
  const Icon = getIcon();
  const iconColor = getIconColor();
  const backgroundColor = getBackgroundColor();

  const getTooltipText = () => TOOLTIP_TEXT[connectionState];

  const tooltipText = getTooltipText();

  const show = connectionState !== "connected";

  return (
    <section
      className={
        show
          ? "absolute top-105 right-9 z-10 flex items-center gap-2"
          : "hidden"
      }
    >
      {showReconnectButton && (
        <button
          className="flex h-8 cursor-pointer items-center rounded bg-[#696EB1] px-3 text-sm font-medium text-white shadow-lg transition-transform hover:scale-105"
          onClick={retry}
          type="button"
        >
          Reconnect
        </button>
      )}

      <div
        className="group relative flex h-8 w-8 items-center justify-center rounded-full shadow-lg"
        style={{ backgroundColor }}
        title={tooltipText}
      >
        <Icon fontSize={16} style={{ color: iconColor }} />

        <div className="pointer-events-none absolute top-full left-0 z-50 mt-2 rounded bg-gray-800 px-2 py-1 text-xs whitespace-nowrap text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          {getTooltipText()}
          <div className="absolute bottom-full left-2 h-0 w-0 border-r-4 border-b-4 border-l-4 border-transparent border-b-gray-800" />
        </div>
      </div>
    </section>
  );
};
